import { useEffect, useState } from 'react';
import axiosInstance from './axiosInstance';
import AuthService from '../services/authService';
import { isLabAssistantStaff } from './labRole';

// Every "view" a signed-in user may be allowed into. The access rules mirror the
// route guards in App.js (RequireAdminAccess, RequireRecoveryAccess, ...), so a
// workspace is only offered when its route would actually let the user in.
export const WORKSPACES = {
  admin: {
    key: 'admin',
    title: 'Admin',
    description: 'Students, batches, timetable, reports and everything day to day.',
    path: '/admin/dashboard',
    accent: '#4f46e5',
    tint: '#eef2ff',
  },
  lecturer: {
    key: 'lecturer',
    title: 'Lecturer',
    description: 'Your classes, attendance, assignments and study materials.',
    path: '/lecturer/dashboard',
    accent: '#0891b2',
    tint: '#ecfeff',
  },
  adminSales: {
    key: 'adminSales',
    title: 'Sales Admin',
    description: 'Sales targets, income and team performance.',
    path: '/admin-sales/dashboard',
    accent: '#d97706',
    tint: '#fffbeb',
  },
  recovery: {
    key: 'recovery',
    title: 'Due Recovery',
    description: 'Pending payments, follow-ups and recovery progress.',
    path: '/recovery/dashboard',
    accent: '#059669',
    tint: '#ecfdf5',
  },
  lab: {
    key: 'lab',
    title: 'Lab',
    description: 'Lab job queue and assistant tasks.',
    path: '/lab/dashboard',
    accent: '#7c3aed',
    tint: '#f5f3ff',
  },
  sales: {
    key: 'sales',
    title: 'Sales',
    description: 'Inquiries, leads and your sales pipeline.',
    path: '/sales/dashboard',
    accent: '#db2777',
    tint: '#fdf2f8',
  },
  tvec: {
    key: 'tvec',
    title: 'TVEC',
    description: 'TVEC registrations, compliance and reporting.',
    path: '/tvec/dashboard',
    accent: '#0284c7',
    tint: '#f0f9ff',
  },
};

const norm = (v) => String(v || '').toLowerCase().replace(/\s+/g, '');

// Returns the workspaces the user can open, in display order.
// `stored` is AuthService.getUser().
export async function getAvailableWorkspaces(stored) {
  const superAdmin = stored?.superAdmin || {};
  const staff = superAdmin.staff || {};
  const isSuperAdmin = Boolean(superAdmin.superAdminId);

  const category = norm(staff.category || staff.staffType || staff.type);
  const role = norm(staff.role);
  const staffType = norm(staff.staffType);

  const isAcademic = category === 'academic';
  const isSalesStaff = category === 'sales';
  const isRecoveryOfficer = staffType === 'recoveryofficer' || role === 'recoveryofficer' || category === 'recovery';
  const isLab = isLabAssistantStaff(staff);
  const isAdminRole = role === 'admin' || role === 'administrator' || staffType === 'admin' || staffType === 'administrator';
  const isManagement = ['management', 'manager'].includes(role) || ['management', 'manager'].includes(staffType);

  let coordinateCount = 0;
  if (!isSuperAdmin) {
    try {
      const resp = await axiosInstance.get('/admin/me/coordinate-courses');
      const data = resp?.data || {};
      const courses = data.coordinateCourses || {};
      const ids = Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : [];
      coordinateCount = Object.keys(courses).length || ids.length;
    } catch (_) {
      coordinateCount = 0;
    }
  }

  const keys = [];

  // Admin (same rule as RequireAdminAccess)
  if (!isLab) {
    if (isSuperAdmin) keys.push('admin');
    else if (coordinateCount > 0 ? !isSalesStaff && !isRecoveryOfficer : !isAcademic && !isSalesStaff && !isRecoveryOfficer) keys.push('admin');
  }

  // Lecturer (RequireLecturerAccess excludes recovery/lab/sales staff)
  if (!isRecoveryOfficer && !isLab && !isSalesStaff && (isSuperAdmin || isAcademic || coordinateCount > 0)) {
    keys.push('lecturer');
  }

  if (isSuperAdmin || isAdminRole) keys.push('adminSales');
  if (isSuperAdmin || isAdminRole || isManagement || isRecoveryOfficer) keys.push('recovery');
  if (isSuperAdmin || isManagement || isLab) keys.push('lab');
  if (isSalesStaff && !isSuperAdmin) keys.push('sales');
  // TVEC: super admin, HOD and management only (same rule as the full admin dashboard).
  if (hasDashboardFullAccess(stored)) keys.push('tvec');

  return keys.map((k) => WORKSPACES[k]);
}

// Where to send the user right after login:
// one workspace -> straight in, several -> the picker, none -> null (caller falls back).
export async function resolveLandingPath(stored) {
  const list = await getAvailableWorkspaces(stored);
  if (list.length === 1) return list[0].path;
  if (list.length > 1) return '/workspaces';
  return null;
}

// Used by every top bar: true when the user has more than one workspace, which is
// when a "Switch workspace" entry makes sense. Cached per login so page changes
// don't refetch.
let cache = { key: null, promise: null };

export function useHasMultipleWorkspaces() {
  const [multi, setMulti] = useState(false);
  useEffect(() => {
    const stored = AuthService.getUser();
    if (!stored?.apiKey) return undefined;
    if (cache.key !== stored.apiKey) {
      cache = { key: stored.apiKey, promise: getAvailableWorkspaces(stored).catch(() => []) };
    }
    let cancelled = false;
    cache.promise.then((list) => { if (!cancelled) setMulti(list.length > 1); });
    return () => { cancelled = true; };
  }, []);
  return multi;
}

// Super admins plus HOD / Management / Manager staff get the full admin dashboard
// (mirrors hasDashboardFullAccess in the backend).
export function hasDashboardFullAccess(stored) {
  const superAdmin = stored?.superAdmin || {};
  if (superAdmin.superAdminId) return true;
  // staff.type is only the login kind ("staff"/"admin"); the real role (HOD, Management...)
  // is staff.staffType, so check every field that can carry it.
  const staff = superAdmin.staff || {};
  return [staff.staffType, staff.role, staff.type].some((v) =>
    ['hod', 'management', 'manager'].includes(String(v || '').trim().toLowerCase()));
}
